import { TrustSignalItem } from '@/configs/product-general-info/product-general-info.config';
import { getStaticContent } from '@/services/get-static-content';
import { resolveMultilingualValue } from '@/utils/resolve-multilingual-value';
import {
  collapseDuplicateInfoSegments,
  resolveStudioPageHref,
} from '@/utils/resolve-studio-page-href';
import { SupportedLocales } from '../core.config';

export type FooterLinkItem = {
  label: string;
  href: string;
  /** When omitted, legacy CMS paths under `/info` are preserved. */
  linkType?: 'external' | 'internal';
};

export type ResolvedFooterLinkTarget =
  | { mode: 'internal'; href: string }
  | { mode: 'external'; href: string };

/** Legacy footer entries stored only a slug; newer studio values may already be full paths. */
function legacyFooterPathToInternalHref(path: string): string {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  let resolved: string;
  // Already under /info or common studio roots — do not prepend /info again.
  if (
    normalized.startsWith('/info/') ||
    normalized === '/info' ||
    normalized.startsWith('/blogs') ||
    normalized.startsWith('/recipes')
  ) {
    resolved = normalized;
  } else {
    resolved = `/info${normalized}`;
  }
  return collapseDuplicateInfoSegments(resolved);
}

export function resolveFooterLinkTarget(item: FooterLinkItem): ResolvedFooterLinkTarget {
  const href = item.href ?? '';
  if (item.linkType === 'internal') {
    return { mode: 'internal', href: resolveStudioPageHref(href) };
  }
  if (item.linkType === 'external') {
    return { mode: 'external', href };
  }
  if (/^https?:\/\//i.test(href) || href.startsWith('mailto:') || href.startsWith('tel:')) {
    return { mode: 'external', href };
  }
  return { mode: 'internal', href: legacyFooterPathToInternalHref(href) };
}

export type FooterSection = {
  title: string;
  items: FooterLinkItem[];
};

export type FooterBanner = {
  logoSrc: string;
  description: string;
  socials: {
    icon?: string;
    iconSrc?: string;
    href: string;
  }[];
};

export type FooterContent = {
  sections: FooterSection[];
  banner: FooterBanner;
  trustSignals?: TrustSignalItem[];
  topBorderColor?: string;
  background?: string;
  contentBackground?: string;
  contentTitleColor?: string;
  contentTextColor?: string;
  contentLinkColor?: string;
  socialIconColor?: string;
};
export type FooterStaticContent = FooterContent;

type StudioFooterData = {
  type: 'FOOTER';
  variant: string;
  properties: {
    style?: {
      topBorderColor?: string;
      contentBackground?: string;
      contentTitleColor?: string;
      contentTextColor?: string;
      contentLinkColor?: string;
      socialIconColor?: string;
      background?: string;
    };
    banner?: {
      logoSrc?: string;
      description?: unknown;
    };
    sections?: {
      items?: Array<{
        title: unknown;
        links: Array<{ label: unknown; href: string; linkType?: 'external' | 'internal' }>;
      }>;
    };
    socials?: {
      items?: Array<{ icon?: string; iconSrc?: string; href: string }>;
    };
    trustSignals?: {
      items?: unknown[];
    };
  };
};

function isStudioFormat(data: unknown): data is StudioFooterData {
  return (
    !!data &&
    typeof data === 'object' &&
    'type' in data &&
    (data as StudioFooterData).type === 'FOOTER' &&
    'properties' in data
  );
}

export const getFooterContent = async (locale: SupportedLocales): Promise<FooterContent> => {
  const rawContent = await getStaticContent('FOOTER');
  const r = (value: unknown) => resolveMultilingualValue(value, locale);

  if (isStudioFormat(rawContent)) {
    const style = rawContent.properties.style ?? {};
    const banner = rawContent.properties.banner ?? {};
    const sectionsGroup = rawContent.properties.sections ?? {};
    const socialsGroup = rawContent.properties.socials ?? {};
    const trustSignalsGroup = rawContent.properties.trustSignals ?? {};

    return {
      banner: {
        logoSrc: String(banner.logoSrc ?? ''),
        description: r(banner.description),
        socials: (socialsGroup.items ?? [])
          .filter(item => item.href && (item.icon || item.iconSrc))
          .map(item => ({ href: item.href, icon: item.icon, iconSrc: item.iconSrc })),
      },
      sections: (sectionsGroup.items ?? []).map(section => ({
        title: r(section.title),
        items: (section.links ?? []).map(link => ({
          label: r(link.label),
          href: link.href,
          ...(link.linkType ? { linkType: link.linkType } : {}),
        })),
      })),
      trustSignals: (Array.isArray(trustSignalsGroup.items) ? trustSignalsGroup.items : [])
        .filter(
          (item): item is { image: string; link?: string } =>
            !!item &&
            typeof item === 'object' &&
            'image' in item &&
            typeof (item as Record<string, unknown>).image === 'string' &&
            !!(item as Record<string, unknown>).image,
        )
        .map(item => ({
          image: item.image,
          link: typeof item.link === 'string' && item.link ? item.link : undefined,
          alt:
            typeof (item as Record<string, unknown>).alt === 'string' &&
            (item as Record<string, unknown>).alt
              ? ((item as Record<string, unknown>).alt as string)
              : undefined,
        })),
      topBorderColor: String(style.topBorderColor ?? 'transparent'),
      contentBackground: String(style.contentBackground ?? 'foreground'),
      contentTitleColor: String(style.contentTitleColor ?? 'default-200'),
      contentTextColor: String(style.contentTextColor ?? 'default-200'),
      contentLinkColor: String(style.contentLinkColor ?? 'default-200'),
      socialIconColor: String(style.socialIconColor ?? 'default-200'),
      background: String(style.background ?? 'foreground'),
    };
  }

  return rawContent as unknown as FooterContent;
};
